import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { useAppDispatch, useAppSelector, RootState } from "../../../store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Select, Skeleton, Spin, Table, Tooltip } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  CapitalizeFirstWord,
  formatCurrency,
  momentFullTime,
  useDebounce,
  formatDeliveryAddress,
} from "../../../helpers/utils";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoDataMessage from "../../Components/NoData";
import { svg } from "../../../Assets/svg";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import NotificationButton from "../../Components/NotificationButton";
import { decryptByText } from "../../../helpers/bycrypt";
import { OrderStatusEnum } from "../Orders/orderTypes";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { fetchInstallationPlanQueryFn } from "../../Redux/installationPlan";
import InstalltionCalnderView from "./Components/InstalltionCalnderView";
import { GetTeamMemberListAPI } from "../../Redux/teamMember";
import FullScreenButton from "../../Components/FullScreenButton";

export const INSTALLATION_PLAN_FILTER_KEY = "installation-plan-filter-key";
export const INSTALLATION_PLAN_SCROLL_KEY = "installation-plan-scroll-key";
export const INSTALLATION_PLAN_REFETCH_KEY = "installation-plan-refetch-key";

// Intercept browser reloads and clear filter persistence so the page starts completely fresh
try {
  const navEntries = performance.getEntriesByType("navigation");
  const isReload =
    navEntries.length > 0 &&
    (navEntries[0] as PerformanceNavigationTiming).type === "reload";
  const isLegacyReload =
    performance.navigation && performance.navigation.type === 1;

  if (isReload || isLegacyReload) {
    sessionStorage.removeItem(INSTALLATION_PLAN_FILTER_KEY);
    sessionStorage.removeItem(INSTALLATION_PLAN_SCROLL_KEY);
    sessionStorage.removeItem(INSTALLATION_PLAN_REFETCH_KEY);
  }
} catch (e) {
  // ignore
}

const getPersistedFilters = () => {
  try {
    const pf = sessionStorage.getItem(INSTALLATION_PLAN_FILTER_KEY);
    return pf ? JSON.parse(pf) : null;
  } catch (e) {
    return null;
  }
};

const InstallationPlan = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.INSTALLATION_PLAN,
  );
  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const _pf = getPersistedFilters();
  const [search, setSearch] = useState(_pf?.search ?? "");
  const debouncedSearch = useDebounce(search, 500);
  const [statusFilter, setStatusFilter] = useState(_pf?.statusFilter ?? "");
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("activeTab");
  const initialTab =
    tabFromUrl === "list" || tabFromUrl === "calendar" ? tabFromUrl : "list";
  const [activeTab, setActiveTab] = useState<"list" | "calendar">(initialTab);

  useEffect(() => {
    const tabFromUrl = searchParams.get("activeTab");
    if (tabFromUrl === "list" || tabFromUrl === "calendar") {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (tab: "list" | "calendar") => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set("activeTab", tab);
      return nextParams;
    });
  };

  const [isMounting, setIsMounting] = useState(true);
  const scrollableDivRef = useRef<HTMLDivElement | null>(null);
  const scrollableDivId = "installation-plan-scrollable-container";
  const pageScrollId = "installation-plan-page-scroll";
  const scrollRestoredRef = useRef(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 20);
    return () => clearTimeout(timer);
  }, []);

  // Save filters to session storage
  useEffect(() => {
    if (!isMounting) {
      sessionStorage.setItem(
        INSTALLATION_PLAN_FILTER_KEY,
        JSON.stringify({ search, statusFilter }),
      );
    }
  }, [search, statusFilter, isMounting]);

  const teamPayload = {
    page: 1,
    limit: 200,
    search: "",
    roleKey: "installation-manager",
  };

  useEffect(() => {
    dispatch(GetTeamMemberListAPI(teamPayload));
  }, []);
  useEffect(() => {
    if (sessionStorage.getItem(INSTALLATION_PLAN_REFETCH_KEY) === "1") {
      sessionStorage.removeItem(INSTALLATION_PLAN_REFETCH_KEY);
      setTimeout(() => {
        sessionStorage.removeItem(INSTALLATION_PLAN_SCROLL_KEY);
      }, 500);
      queryClient.removeQueries({ queryKey: ["installationPlan"] });
    }
  }, [queryClient]);

  const queryFilters = {
    search: debouncedSearch || "",
    status: statusFilter,
  };

  const {
    data: queryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isQueryLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["installationPlan", queryFilters],
    queryFn: ({ pageParam = 1, signal }) => {
      const params: any = {
        page: pageParam as number,
        limit: 25,
        search: queryFilters.search,
      };
      if (queryFilters.status) {
        params.status = queryFilters.status;
      }
      return fetchInstallationPlanQueryFn(params, signal);
    },
    getNextPageParam: (lastPage: any, allPages) => {
      const fetched = allPages.reduce(
        (acc: number, p: any) => acc + (p?.data?.length || 0),
        0,
      );
      const total = lastPage?.count || 0;
      return fetched < total ? allPages.length + 1 : undefined;
    },
    staleTime: 5000,
    enabled: activeTab === "list",
    initialPageParam: 1,
  });

  // Start 5-second countdown ONLY AFTER all active pages finish fetching
  useEffect(() => {
    if (activeTab !== "list") return;
    if (isFetching) return;

    const timer = setTimeout(() => {
      refetch();
    }, 5000);

    return () => clearTimeout(timer);
  }, [isFetching, activeTab, refetch]);

  const installationPlanData = useMemo(() => {
    if (!queryData?.pages) return [];
    return queryData.pages.flatMap((page: any) => page.data || []);
  }, [queryData]);

  const statusCountsRef = useRef<any>({});

  const statusCounts = useMemo(() => {
    const fetchedCounts = (queryData?.pages?.[0] as any)?.statusCounts;
    if (fetchedCounts && Object.keys(fetchedCounts).length > 0) {
      statusCountsRef.current = fetchedCounts;
    }
    return statusCountsRef.current;
  }, [queryData]);

  const totalJobsCount = useMemo(() => {
    return Object.values(statusCounts).reduce(
      (acc: number, curr: any) => acc + (Number(curr) || 0),
      0,
    );
  }, [statusCounts]);

  // Scroll restoration
  useLayoutEffect(() => {
    if (
      !scrollRestoredRef.current &&
      installationPlanData.length > 0 &&
      !isQueryLoading &&
      !isMounting
    ) {
      const savedScrollPos = sessionStorage.getItem(
        INSTALLATION_PLAN_SCROLL_KEY,
      );
      if (savedScrollPos && scrollableDivRef.current) {
        const scrollPos = parseInt(savedScrollPos, 10);
        // Find the actual scrollable element (either card-body or an internal InfiniteScroll div)
        const scrollableElement =
          Array.from(scrollableDivRef.current.querySelectorAll("*")).find(
            (el: any) => el.scrollHeight > el.offsetHeight,
          ) || scrollableDivRef.current;

        if (scrollableElement) {
          scrollableElement.scrollTop = scrollPos;
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (scrollableElement) {
                scrollableElement.scrollTop = scrollPos;
                scrollableElement.dispatchEvent(
                  new Event("scroll", { bubbles: true }),
                );
              }
            }, 100);
          });
        }
        window.scrollTo(0, scrollPos); // Fallback for window-level scroll
      }
      sessionStorage.removeItem(INSTALLATION_PLAN_SCROLL_KEY);
      scrollRestoredRef.current = true;
    }
  }, [installationPlanData, isQueryLoading, isMounting]);

  // Reset scroll restoration when filters change
  useEffect(() => {
    if (!isMounting) {
      scrollRestoredRef.current = false;
      if (scrollableDivRef.current) {
        scrollableDivRef.current.scrollTop = 0;
      }
      // Clear inactive caches to force fresh fetch on new filters
      queryClient.removeQueries({
        queryKey: ["installationPlan"],
        type: "inactive",
      });
    }
  }, [debouncedSearch, statusFilter, queryClient]);

  const PlanHiredCategory = {
    scissor_lift: "scissor_lift",
    flt: "fork_lift_truck",
    reach_truck: "reach_truck",
  };

  const StatusEnum = {
    init: "-",
    "awaiting-customer-confirmation": "Awaiting Customer Confirmation",
    confirmed: "Confirmed",
    "ready-for-install": "Ready For Install",
    "on-site": "On Site",
    issue: "Issue / Blocked",
    pending: "Pending",
    completed: "Completed",
  };
  const statusClassMap = {
    "awaiting-customer-confirmation": "awaitingCustomerConfirmationStatus",
    confirmed: "confirmedStatus",
    "ready-for-install": "readyForInstallStatus",
    "on-site": "onSiteStatus",
    issue: "issueStatus",
    pending: "pendingStatus",
    completed: "approvedStatus",
  };
  const columns = [
    {
      title: "Customer",
      dataIndex: "order_id",
      key: "order_id",
      width: 250,
      render: (order_id: any, record: any) => {
        return order_id ? (
          <div className="d-flex flex-column gap-1">
            <div>
              {CapitalizeFirstWord(order_id?.customer_snapshot?.first_name) +
                " " +
                CapitalizeFirstWord(order_id?.customer_snapshot?.last_name)}
            </div>
            <div className="fs-7">
              {formatDeliveryAddress(
                order_id?.customer_snapshot?.delivery_address,
              )}
            </div>
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Order No",
      dataIndex: "order_id",
      key: "order_id",
      width: 80,
      render: (order_id: any, record: any) => {
        return order_id?.order_id ? (
          <span
            className="text-decoration-underline "
            onClick={() => {
              if (scrollableDivRef.current) {
                const scrollableElement =
                  Array.from(
                    scrollableDivRef.current.querySelectorAll("*"),
                  ).find((el: any) => el.scrollHeight > el.offsetHeight) ||
                  scrollableDivRef.current;
                sessionStorage.setItem(
                  INSTALLATION_PLAN_SCROLL_KEY,
                  String(scrollableElement?.scrollTop || window.scrollY),
                );
              }
              navigate(`/orders/${order_id?._id}`, {
                state: { backPath: "/installation-plan" },
              });
            }}
            role="button"
          >
            {order_id?.order_id}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Product",
      dataIndex: "categories_snapshot",
      key: "categories_snapshot",
      width: 270,
      render: (categories_snapshot: any, record: any) => {
        const isArray =
          categories_snapshot && Array.isArray(categories_snapshot)
            ? categories_snapshot
            : [categories_snapshot];
        return isArray?.length > 0
          ? isArray
              ?.map((categories_snapshot: any) => categories_snapshot?.name)
              .join(", ")
          : "-";
      },
    },
    {
      title: "Plant Hire Required?",
      dataIndex: "is_plant_hire",
      key: "is_plant_hire",
      width: 100,
      render: (is_plant_hire: any, record: any) => {
        return is_plant_hire ? (
          <span className="text-success">Yes</span>
        ) : (
          <span className="text-danger">No</span>
        );
      },
    },
    {
      title: "Plant Hire Company",
      dataIndex: "clone_plant_hire_company",
      key: "clone_plant_hire_company",
      width: 150,
      render: (clone_plant_hire_company: any) => {
        return clone_plant_hire_company?.length > 0
          ? clone_plant_hire_company?.map((item: any) => item?.name).join(", ")
          : "-";
      },
    },
    {
      title: "Plant Hired",
      children: [
        {
          title: "Scissor Lift",
          dataIndex: "services",
          key: "services",
          render: (services: any, record: any) => {
            const findedService = services?.filter(
              (elem: any) =>
                elem?.planning?.plan_hire_category ==
                PlanHiredCategory?.scissor_lift,
            );
            return findedService?.length > 0
              ? findedService?.map((item: any, index: any) => {
                  return (
                    <div key={item?._id}>
                      {item?.planning?.name} X {item?.planning?.quantity}
                      {index == findedService?.length - 1 ? "" : ","}
                    </div>
                  );
                })
              : "-";
          },
        },
        {
          title: "F.L.T",
          dataIndex: "services",
          key: "services",
          render: (services: any, record: any) => {
            const findedService = services?.filter(
              (elem: any) =>
                elem?.planning?.plan_hire_category == PlanHiredCategory?.flt,
            );
            return findedService?.length > 0
              ? findedService?.map((item: any, index: any) => {
                  return (
                    <div key={item?._id}>
                      {item?.planning?.name} X {item?.planning?.quantity}
                      {index == findedService?.length - 1 ? "" : ","}
                    </div>
                  );
                })
              : "-";
          },
        },
        {
          title: "Reach Truck",
          dataIndex: "services",
          key: "services",
          render: (services: any, record: any) => {
            const findedService = services?.filter(
              (elem: any) =>
                elem?.planning?.plan_hire_category ==
                PlanHiredCategory?.reach_truck,
            );
            return findedService?.length > 0
              ? findedService?.map((item: any, index: any) => {
                  return (
                    <div key={item?._id}>
                      {item?.planning?.name} X {item?.planning?.quantity}
                      {index == findedService?.length - 1 ? "" : ","}
                    </div>
                  );
                })
              : "-";
          },
        },
      ],
    },
    {
      title: "Install Team",
      dataIndex: "employee_ids",
      key: "employee_ids",
      width: 200,
      render: (employee_ids: any) => {
        return employee_ids?.length > 0
          ? employee_ids
              ?.map((item: any) => {
                const name = `${CapitalizeFirstWord(
                  decryptByText(item?.first_name),
                )} ${CapitalizeFirstWord(decryptByText(item?.last_name))}`;
                return name;
              })
              .join(", ")
          : "-";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      render: (status: any) => {
        return status && status != "init" ? (
          <span
            className={`d-inline-flex align-items-center billStatus ${
              statusClassMap[status as keyof typeof statusClassMap] || ""
            }`}
          >
            <span className="">{svg.dotIcon}</span>
            {StatusEnum[status as keyof typeof StatusEnum]}
          </span>
        ) : (
          "-"
        );
      },
    },
    {
      title: "Start Date",
      dataIndex: "start_at",
      key: "start_at",
      width: 130,
      render: (start_at: any) => {
        return start_at ? momentFullTime(start_at) : "-";
      },
    },
    {
      title: "End Date",
      dataIndex: "end_at",
      key: "end_at",
      width: 130,
      render: (end_at: any) => {
        return end_at ? momentFullTime(end_at) : "-";
      },
    },
    {
      title: "Installation Cost",
      dataIndex: "installation_cost",
      key: "installation_cost",
      width: 110,
      render: (installation_cost: any, record: any) => {
        return installation_cost
          ? formatCurrency(
              Number(installation_cost),
              record?.currency,
              record?.conversion_rate,
            )
          : "-";
      },
    },
    {
      title: "Plant Hire Cost",
      dataIndex: "plant_hire_cost",
      key: "plant_hire_cost",
      width: 110,
      render: (plant_hire_cost: any, record: any) => {
        return plant_hire_cost
          ? formatCurrency(
              Number(plant_hire_cost),
              record?.currency,
              record?.conversion_rate,
            )
          : "-";
      },
    },
    {
      title: "Actual Installation Cost",
      dataIndex: "act_installation_cost",
      key: "act_installation_cost",
      width: 110,
      render: (act_installation_cost: any, record: any) => {
        return act_installation_cost != undefined ||
          act_installation_cost != null
          ? formatCurrency(
              Number(act_installation_cost),
              record?.currency,
              record?.conversion_rate,
            )
          : "-";
      },
    },
    {
      title: "Actual Plant Hire Cost",
      dataIndex: "act_plant_hire_cost",
      key: "act_plant_hire_cost",
      width: 110,
      render: (act_plant_hire_cost: any, record: any) => {
        return act_plant_hire_cost != undefined || act_plant_hire_cost != null
          ? formatCurrency(
              Number(act_plant_hire_cost),
              record?.currency,
              record?.conversion_rate,
            )
          : "-";
      },
    },
    {
      title: "Profit / Loss",
      dataIndex: "profit_loss",
      key: "profit_loss",
      width: 130,
      render: (profit_loss: any, record: any) => {
        if (record?.status != "completed") {
          return "-";
        }
        const oldTotal = record?.installation_cost + record?.plant_hire_cost;
        const newTotal =
          (record?.act_installation_cost || 0) +
          (record?.act_plant_hire_cost || 0);
        const profit = oldTotal - newTotal;
        if (profit > 0) {
          return (
            <span className="text-success fw-semibold">
              {formatCurrency(
                Number(profit),
                record?.currency,
                record?.conversion_rate,
              )}
            </span>
          );
        } else if (profit < 0) {
          return (
            <span className="text-danger fw-semibold">
              {formatCurrency(
                Number(Math.abs(profit)),
                record?.currency,
                record?.conversion_rate,
              )}
            </span>
          );
        } else {
          return formatCurrency(
            Number(0),
            record?.currency,
            record?.conversion_rate,
          );
        }
      },
    },
    {
      title: "Next Action",
      dataIndex: "next_action",
      key: "next_action",
      width: 150,
      render: (next_action: any, record: any) => {
        return next_action || "-";
      },
    },
    ...(USER_PERMISSIONS?.MODIFY
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            fixed: "right" as const,
            render: (_: any, record: any) => {
              return (
                <div className="d-flex align-items-center gap-2">
                  <Tooltip title="View Details">
                    <span
                      role="button"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        if (scrollableDivRef.current) {
                          const scrollableElement =
                            Array.from(
                              scrollableDivRef.current.querySelectorAll("*"),
                            ).find(
                              (el: any) => el.scrollHeight > el.offsetHeight,
                            ) || scrollableDivRef.current;
                          sessionStorage.setItem(
                            INSTALLATION_PLAN_SCROLL_KEY,
                            String(
                              scrollableElement?.scrollTop || window.scrollY,
                            ),
                          );
                        }
                        navigate(
                          `/installation-plan/view-detail/${record?._id}`,
                          { state: { activeTab } },
                        );
                      }}
                      className="svg-32"
                    >
                      {svg?.viewIcon}
                    </span>
                  </Tooltip>
                  <Tooltip title="View Issue">
                    <span
                      role="button"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        if (scrollableDivRef.current) {
                          const scrollableElement =
                            Array.from(
                              scrollableDivRef.current.querySelectorAll("*"),
                            ).find(
                              (el: any) => el.scrollHeight > el.offsetHeight,
                            ) || scrollableDivRef.current;
                          sessionStorage.setItem(
                            INSTALLATION_PLAN_SCROLL_KEY,
                            String(
                              scrollableElement?.scrollTop || window.scrollY,
                            ),
                          );
                        }
                        navigate(
                          `/installation-plan/view-detail/${record?._id}?tab=Issues`,
                          { state: { activeTab } },
                        );
                      }}
                      className="svg-32"
                    >
                      {svg?.issueIcon}
                    </span>
                  </Tooltip>
                  <Tooltip title="View Readiness Checklist">
                    <span
                      role="button"
                      onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        if (scrollableDivRef.current) {
                          const scrollableElement =
                            Array.from(
                              scrollableDivRef.current.querySelectorAll("*"),
                            ).find(
                              (el: any) => el.scrollHeight > el.offsetHeight,
                            ) || scrollableDivRef.current;
                          sessionStorage.setItem(
                            INSTALLATION_PLAN_SCROLL_KEY,
                            String(
                              scrollableElement?.scrollTop || window.scrollY,
                            ),
                          );
                        }
                        navigate(
                          `/installation-plan/view-detail/${record?._id}?tab=ReadinessChecklist`,
                          { state: { activeTab } },
                        );
                      }}
                      className="svg-32"
                    >
                      {svg?.readinessCheck}
                    </span>
                  </Tooltip>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return USER_PERMISSIONS?.READ ? (
    <div id={pageScrollId} className="page-content h-100 px-40 py-40">
      <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
        {/* Header — matches Quotations pattern */}
        <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
          <h2 className="text-black fw-bolder page-title mb-0">
            Installation Plan
          </h2>

          {/* Right-side controls — status dropdown + tab toggle + fullscreen + notification */}
          <div className="d-flex gap-md-3 gap-2 align-items-center ms-auto">
            {/* Status filter — hidden on mobile, shown on desktop */}
            <div className="d-none d-md-flex align-items-center gap-2">
              <label className="fs-6 fw-semibold text-secondary mb-0">
                Status
              </label>
              <div className="whatsBannedDropdown filterCommonDropdown plannedStatusDropdown mw-100 d-inline-block">
                <Select
                  value={statusFilter}
                  className="form-select form-select-lg"
                  popupClassName="full-text-select-dropdown"
                  onChange={(value) => {
                    setStatusFilter(value);
                  }}
                  rootClassName="installationPlanStatus"
                >
                  <Select.Option value={""}>All</Select.Option>
                  <Select.Option key="awaiting-customer-confirmation" value="awaiting-customer-confirmation">
                    <div style={{ color: "#b45309" }}>Awaiting Customer Confirmation</div>
                  </Select.Option>
                  <Select.Option key="confirmed" value="confirmed">
                    <div style={{ color: "#1d4ed8" }}>Confirmed</div>
                  </Select.Option>
                  <Select.Option key="ready-for-install" value="ready-for-install">
                    <div style={{ color: "#15803d" }}>Ready For Install</div>
                  </Select.Option>
                  <Select.Option key="on-site" value="on-site">
                    <div style={{ color: "#0f766e" }}>On Site</div>
                  </Select.Option>
                  <Select.Option key="issue" value="issue">
                    <div style={{ color: "#e11d48" }}>Issue / Blocked</div>
                  </Select.Option>
                  <Select.Option key="pending" value="pending">
                    <div style={{ color: "#4b5563" }}>Pending</div>
                  </Select.Option>
                  <Select.Option key="completed" value="completed">
                    <div className="text-success">Completed</div>
                  </Select.Option>
                </Select>
              </div>
            </div>

            {/* Tab toggle: List / Calendar */}
            <div className="d-flex align-items-center bg-white rounded-1 border border-black cale-list-btn">
              <button
                onClick={() => handleTabChange("list")}
                className="btn btn-sm p-0 rounded-0 transition-all d-flex align-items-center justify-content-center border-0"
                style={{
                 
                  backgroundColor: activeTab === "list" ? "#231F20" : "transparent",
                  color: activeTab === "list" ? "#ffffff" : "#231F20",
                }}
                title="List View"
              >
                {svg?.installationList}
              </button>
              <button
                onClick={() => handleTabChange("calendar")}
                className="btn btn-sm p-0 rounded-0 transition-all d-flex align-items-center justify-content-center border-0"
                style={{
                  backgroundColor: activeTab === "calendar" ? "#231F20" : "transparent",
                  color: activeTab === "calendar" ? "#ffffff" : "#231F20",
                }}
                title="Calendar View"
              >
                {svg?.installationCalender}
              </button>
            </div>

            <FullScreenButton />
            <NotificationButton />
          </div>
        </div>

        {/* Mobile-only status filter row */}
        <div className="d-flex d-md-none align-items-center gap-2 mt-2 installation-plan-mobile-status">
          <label className="fs-6 mob-fs-6 fw-semibold text-secondary mb-0 text-nowrap">
            Status
          </label>
          <div className="whatsBannedDropdown filterCommonDropdown plannedStatusDropdown d-inline-block">
            <Select
              value={statusFilter}
              className="form-select form-select-lg"
              popupClassName="installationPlanStatus full-text-select-dropdown"
              onChange={(value) => {
                setStatusFilter(value);
              }}
              rootClassName="installationPlanStatus"
            >
              <Select.Option value={""}>All</Select.Option>
              <Select.Option key="awaiting-customer-confirmation" value="awaiting-customer-confirmation">
                <div style={{ color: "#b45309" }}>Awaiting Customer Confirmation</div>
              </Select.Option>
              <Select.Option key="confirmed" value="confirmed">
                <div style={{ color: "#1d4ed8" }}>Confirmed</div>
              </Select.Option>
              <Select.Option key="ready-for-install" value="ready-for-install">
                <div style={{ color: "#15803d" }}>Ready For Install</div>
              </Select.Option>
              <Select.Option key="on-site" value="on-site">
                <div style={{ color: "#0f766e" }}>On Site</div>
              </Select.Option>
              <Select.Option key="issue" value="issue">
                <div style={{ color: "#e11d48" }}>Issue / Blocked</div>
              </Select.Option>
              <Select.Option key="pending" value="pending">
                <div style={{ color: "#4b5563" }}>Pending</div>
              </Select.Option>
              <Select.Option key="completed" value="completed">
                <div className="text-success">Completed</div>
              </Select.Option>
            </Select>
          </div>
        </div>
      </div>
      {activeTab === "calendar" ? (
        <InstalltionCalnderView statusFilter={statusFilter} />
      ) : (
        <>
          <div className="mb-3">
            <div className="row g-3">
              {[
                {
                  key: "",
                  label: "ALL JOBS",
                  count: totalJobsCount,
                  color: "#0f172a",
                },
                {
                  key: "awaiting-customer-confirmation",
                  label: "AWAITING",
                  count: statusCounts["awaiting-customer-confirmation"] || 0,
                  color: "#92400e",
                },
                {
                  key: "confirmed",
                  label: "CONFIRMED",
                  count: statusCounts["confirmed"] || 0,
                  color: "#1d4ed8",
                },
                {
                  key: "ready-for-install",
                  label: "READY",
                  count: statusCounts["ready-for-install"] || 0,
                  color: "#155e34",
                },
                {
                  key: "on-site",
                  label: "ON SITE",
                  count: statusCounts["on-site"] || 0,
                  color: "#14532d",
                },
                {
                  key: "completed",
                  label: "COMPLETE",
                  count: statusCounts["completed"] || 0,
                  color: "#155e34",
                },
                {
                  key: "issue",
                  label: "BLOCKED",
                  count: statusCounts["issue"] || 0,
                  color: "#9f1239",
                },
                {
                  key: "pending",
                  label: "PENDING",
                  count: statusCounts["pending"] || 0,
                  color: "#6b7280",
                },
              ].map((box: any, index: any) => {
                const isActive = statusFilter === box.key;
                return (
                  <div
                    key={box?.key}
                    className="col-6 col-md-3 col-xl-auto"
                    style={{ flex: "1 1 150px", minWidth: "140px" }}
                  >
                    <div
                      className="card border-0 shadow-sm rounded-3 h-100 cursor-pointer"
                      style={{ transition: "box-shadow 0.2s" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(0,0,0,0.12)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.boxShadow = "")
                      }
                      onClick={() => setStatusFilter(box.key)}
                    >
                      <div
                        className="card-body p-3 rounded-3"
                        style={{
                          minHeight: "80px",
                          border: "1px solid grey",
                          borderColor: isActive ? "#000000" : "#e5e7eb",
                          borderWidth: isActive ? "1.5px" : "1px",
                        }}
                      >
                        <div
                          className="fw-semibold mb-2"
                          style={{
                            fontSize: "14px",
                            color: box.color,
                            letterSpacing: "0.2px",
                          }}
                        >
                          {box.label}
                        </div>
                        <div
                          className="fw-bold d-flex align-items-center justify-content-between"
                          style={{
                            fontSize: "28px",
                            lineHeight: 1,
                            color: "#1d1d1d",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            {totalJobsCount == 0 && isQueryLoading ? (
                              <Skeleton.Button
                                active={true}
                                size={"default"}
                                shape={"default"}
                                block={false}
                              />
                            ) : (
                              box.count
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`card page-table-card border-0 shadow rounded-4 installation-plan-table-card ${isMobile ? "" : "flex-fill overflow-hidden d-flex flex-column"}`}>
            <div
              className={`card-body ${isMobile ? "" : "overflow-y-auto"}`}
              id={scrollableDivId}
              ref={scrollableDivRef}
            >
              {(isQueryLoading && !installationPlanData.length) ||
              isMounting ? (
                <FullScreenLoader />
              ) : installationPlanData?.length === 0 ? (
                <NoDataMessage
                  icon={svg?.noInstallationPlan}
                  text="You do not have any installation plan yet."
                />
              ) : (
                <InfiniteScroll
                  dataLength={installationPlanData.length}
                  next={fetchNextPage}
                  hasMore={!!hasNextPage}
                  loader={
                    <div className="text-center p-2">
                      <Spin />
                    </div>
                  }
                  scrollableTarget={isMobile ? pageScrollId : scrollableDivId}
                  height={isMobile ? undefined : "100%"}
                  style={isMobile ? {} : { overflow: "auto" }}
                >
                  <Table
                    dataSource={installationPlanData}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    className="custom-table InstallationTable"
                    bordered
                    rowKey={(record) => record._id}
                    onRow={(record) => ({
                      onClick: (e) => {
                        navigate(
                          `/installation-plan/view-detail/${record?._id}`,
                          { state: { activeTab } },
                        );
                      },
                    })}
                  />
                </InfiniteScroll>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default InstallationPlan;
